import { Component, Event, EventEmitter, h, Host, Listen, Method, Prop, State, Watch } from '@stencil/core'

import { IconName } from '../../icons'
import { ColorsType, FontSizeType } from '../../tailwind'
import { classes } from '../../utils/css'

@Component({
  tag: 'ui-input',
})
export class Input {
  @Prop() value: string | number | string[]
  @Prop() rounded = false
  @Prop() required: boolean
  @Prop() name: string
  @Prop() disabled: boolean
  @Prop() type: string
  @Prop() placeholder: string
  @Prop() pattern: string
  @Prop() maxLength: number
  @Prop() autofocus: boolean
  @Prop() autoselect: boolean
  @Prop() validationError: string
  @Prop() color: ColorsType = 'surface-highest'
  @Prop() size: FontSizeType = 'base'
  @Prop() icon: IconName
  @Prop() renderValidationErrors = true
  @Prop() bordered = true
  @Prop() align: 'start' | 'end' | 'center' | 'baseline' | 'stretch' = 'center'
  @Prop() outlined = true

  @Event() inputValidationError: EventEmitter<string>

  @State() pristine = true
  @State() invalid = false

  inputEl: HTMLInputElement

  get errorMessage() {
    return this.invalid && (this.validationError || (this.inputEl && this.inputEl.validationMessage))
  }

  @Watch('invalid')
  setAria() {
    if (this.invalid) {
      this.inputEl.setAttribute('aria-invalid', '')
    } else {
      this.inputEl.removeAttribute('aria-invalid')
    }
    this.inputValidationError.emit(this.errorMessage)
  }

  @Listen('input')
  onInput() {
    if (this.invalid || !this.pristine) {
      this.invalid = !this.inputEl.validity.valid
    }
  }

  @Method()
  async clear() {
    this.inputEl.value = ''
    this.pristine = true
    this.invalid = false
  }

  componentDidLoad() {
    if (this.autofocus) {
      this.inputEl.focus()
    }
    if (this.autoselect) {
      this.inputEl.focus()
      this.inputEl.select()
    }
  }

  render() {
    return (
      <Host
        class={`
          relative flex
          bg-transparent truncate group leading-none items-${this.align}
          ${classes(this.outlined, 'focus-within:shadow-outline')}
          ${classes(this.bordered, `border border-${this.color}`)}
          ${classes(this.disabled, 'pointer-events-none opacity-50')}
          ${classes(this.invalid, 'border border-info-error')}
          ${classes(this.rounded, 'rounded-full', 'rounded-md')}
        `}
        style={{ outline: 'none', padding: '.5em .7em' }}
      >
        {this.icon && (
          <ui-icon
            icon={this.icon}
            style={{ marginRight: '.5em', marginLeft: '-.1em' }}
            class='my-auto opacity-50 transition duration-200'
          />
        )}
        <input
          ref={(el) => (this.inputEl = el)}
          name={this.name}
          type={this.type}
          value={this.value}
          required={this.required}
          disabled={this.disabled}
          placeholder={this.placeholder}
          pattern={this.pattern}
          maxLength={this.maxLength}
          class={`
            bg-transparent flex-grow truncate
            text-${this.size} placeholder-surface-higher
            ${classes(this.invalid, ' text-info-error')}
          `}
          style={{ outline: 'none' }}
          onInvalid={(e: Event) => {
            e.preventDefault()
            this.pristine = false
            this.invalid = true
          }}
        />
        <slot />
        <em
          hidden={!this.renderValidationErrors || !(this.invalid && !this.pristine)}
          class='block mt-2 text-2xs antialiased not-italic font-medium text-info-error'
        >
          {this.errorMessage}
        </em>
      </Host>
    )
  }
}
